import type { ConfirmDeliveryLinePayload } from "../api/types";

/**
 * G-121 S2 (OD-128): what a "Report difference" form sends. ONE rule for the
 * shop tablet and the CRM delivery page — each screen only words the problem
 * in its own language.
 *
 * The form asks one thing: how many are different. Unticked, they are
 * MISSING (they did not arrive); ticked, they ARRIVED DAMAGED — still
 * received and stocked, and a photo is required (OD-125). The server holds
 * the same checks; this saves the round trip.
 */
export type DifferenceProblem = "count" | "tooMany" | "photo";

export type DifferenceResult =
  | { ok: true; payload: Pick<ConfirmDeliveryLinePayload, "receivedQty" | "damagedQty"> }
  | { ok: false; problem: DifferenceProblem };

export function differencePayload(input: {
  /** The line's expected quantity, as the API sends it. */
  expectedQty: string;
  /** What the person typed. */
  count: string;
  damaged: boolean;
  hasPhoto: boolean;
}): DifferenceResult {
  const expected = Number(input.expectedQty);
  const count = Number(input.count);
  if (input.count.trim() === "" || !Number.isFinite(count) || count <= 0) {
    return { ok: false, problem: "count" };
  }
  if (count > expected) return { ok: false, problem: "tooMany" };
  if (input.damaged && !input.hasPhoto) return { ok: false, problem: "photo" };
  return {
    ok: true,
    payload: input.damaged
      ? { receivedQty: expected, damagedQty: count }
      : // Three decimals, as the API stores them: 2.4 − 0.1 must be 2.3.
        { receivedQty: Number((expected - count).toFixed(3)) },
  };
}
